import type { ConnectionState } from '../types';
import './ConnectionStatusIndicator.css';

export interface ConnectionStatusIndicatorProps {
  connectionState: ConnectionState;
  reconnectAttempt?: number;
  onReconnect?: () => void;
}

/**
 * Connection Status Indicator Component
 * Displays the current WebSocket connection state with visual indicators
 * and provides manual reconnection capability
 */
export function ConnectionStatusIndicator({
  connectionState,
  reconnectAttempt = 0,
  onReconnect,
}: ConnectionStatusIndicatorProps) {
  const getStatusConfig = () => {
    switch (connectionState) {
      case 'connected':
        return {
          className: 'status-connected',
          label: 'Connected',
          icon: '●',
        };
      case 'connecting':
        return {
          className: 'status-connecting',
          label: 'Connecting...',
          icon: '◐',
        };
      case 'reconnecting':
        return {
          className: 'status-reconnecting',
          label: `Reconnecting... (Attempt ${reconnectAttempt})`,
          icon: '◐',
        };
      case 'disconnected':
        return {
          className: 'status-disconnected',
          label: 'Disconnected',
          icon: '○',
        };
      default:
        return {
          className: 'status-disconnected',
          label: 'Unknown',
          icon: '?',
        };
    }
  };

  const config = getStatusConfig();
  const showReconnectButton = connectionState === 'disconnected' && onReconnect;

  return (
    <div className={`connection-status-indicator ${config.className}`}>
      <span className="status-icon" aria-hidden="true">
        {config.icon}
      </span>
      <span className="status-label">{config.label}</span>
      {showReconnectButton && (
        <button
          className="reconnect-button"
          onClick={onReconnect}
          aria-label="Reconnect to WebSocket"
        >
          Reconnect
        </button>
      )}
    </div>
  );
}
